/**
 * The screen endpoints.
 *
 * There is no create here on purpose. A screen comes into being when a TV is
 * paired and its code is claimed; the panel never invents one, and the page says
 * so rather than showing a button that could not work.
 *
 * The update is a patch of only what changed. `screenUpdateSchema` makes every
 * field optional, so omitting one leaves it alone — which matters because the
 * fleet row does not carry the recorded screen size, and sending a size the form
 * never actually read would overwrite a real measurement with a guess.
 */

import type { screenUpdateSchema } from '@pbtv/shared';
import type { FleetScreen, RotationDegrees, ScreenSchedule } from '@pbtv/shared';
import type { z } from 'zod';
import { apiRequest, requestJson } from './api.js';
import { fleetListSchema, screenEnvelopeSchema } from './schemas.js';
import type { ScreenRecord } from './schemas.js';

export type ScreenPatch = z.infer<typeof screenUpdateSchema>;

/** What the edit form currently holds. `sizeInches` is raw text from the input. */
export interface ScreenFormValues {
  name: string;
  sizeInches: string;
  rotationDegrees: RotationDegrees;
  autoStart: boolean;
  /** True when this screen ignores its store's hours and runs around the clock. */
  alwaysOn: boolean;
}

/** What it held when it opened. `sizeInches` is null while the size is unknown. */
export interface ScreenBaseline extends Omit<ScreenFormValues, 'sizeInches'> {
  sizeInches: string | null;
}

/**
 * The request body for a screen edit: only the fields that actually changed.
 *
 * Pure, exported and living here rather than inside the form, so that a test can
 * hold it against the literal body the API route accepts. That pairing is the
 * house rule — three milestones shipped a client/server mismatch because each side
 * tested the shape it had invented — and this route had no such test until now,
 * because the logic was trapped inside a `.tsx` the node test runner cannot load.
 *
 * Omitting an untouched field is not an optimisation either. The fleet row does
 * not carry the recorded screen size, so sending one the form never read would
 * overwrite a real measurement with a guess; and sending `autoStart` on every save
 * would let an unrelated rename quietly re-assert a stale idea of whether a
 * television is allowed to turn itself on. The same holds for `alwaysOn`, where
 * a stale re-assert would put a window screen back onto shop hours.
 */
export function buildScreenPatch(next: ScreenFormValues, baseline: ScreenBaseline): ScreenPatch {
  const patch: ScreenPatch = {};
  const trimmedName = next.name.trim();
  const trimmedSize = next.sizeInches.trim();

  if (trimmedName !== baseline.name) patch.name = trimmedName;
  if (next.rotationDegrees !== baseline.rotationDegrees) {
    patch.rotationDegrees = next.rotationDegrees;
  }
  if (next.autoStart !== baseline.autoStart) patch.autoStart = next.autoStart;
  if (next.alwaysOn !== baseline.alwaysOn) patch.alwaysOn = next.alwaysOn;

  if (baseline.sizeInches !== null && trimmedSize !== baseline.sizeInches) {
    patch.sizeInches = trimmedSize === '' ? null : Number(trimmedSize);
  } else if (baseline.sizeInches === null && trimmedSize !== '') {
    patch.sizeInches = Number(trimmedSize);
  }

  return patch;
}

export const FLEET_QUERY_KEY = ['admin', 'screens'] as const;

export function screenQueryKey(screenId: string): readonly string[] {
  return ['admin', 'screens', screenId];
}

export async function listFleet(signal?: AbortSignal): Promise<FleetScreen[]> {
  return apiRequest(fleetListSchema, '/admin/screens', { signal });
}

export async function getScreen(screenId: string, signal?: AbortSignal): Promise<ScreenRecord> {
  return apiRequest(screenEnvelopeSchema, `/admin/screens/${encodeURIComponent(screenId)}`, {
    signal,
  });
}

export async function updateScreen(screenId: string, patch: ScreenPatch): Promise<void> {
  await requestJson(`/admin/screens/${encodeURIComponent(screenId)}`, {
    method: 'PUT',
    body: patch,
  });
}

/**
 * What the screen plays.
 *
 * Creating a playlist does not put it on a television — this is the call that
 * connects the two, and until it is made a claimed screen with a full library
 * still shows the built-in "waiting for content" card. `rules` carries the
 * dayparts; sending an empty array means the default playlist runs all day,
 * which is the ordinary case.
 */
export async function setScreenSchedule(
  screenId: string,
  schedule: ScreenSchedule,
): Promise<void> {
  await requestJson(`/admin/screens/${encodeURIComponent(screenId)}/schedule`, {
    method: 'PUT',
    body: schedule,
  });
}

/** Un-pairs the device and deletes the record; the TV falls back to pairing. */
export async function deleteScreen(screenId: string): Promise<void> {
  await requestJson(`/admin/screens/${encodeURIComponent(screenId)}`, { method: 'DELETE' });
}
